import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import type {
  ConnectedAppAccessLevel,
  OrganisationConnectedApp,
  OrganisationConnectedAppGrant,
  OrganisationConnectedAppGrantResponse,
  OrganisationConnectedAppsResponse,
  OrganisationConnection,
  OrganisationConnectionResponse,
  OrganisationGroup,
  OrganisationMember,
  SetOrganisationConnectedAppGrantRequest,
  UpdateOrganisationConnectionRequest,
  OrganisationConnectOutcome,
  OrganisationConnectStartResponse,
} from '@dreamai/types';
import { apiErrorMessage, formatDate } from './organisationLabels';
import OrganisationConnectedAppIdentities from './OrganisationConnectedAppIdentities';
import { AssistantCopy, assistantCopy } from './organisationAssistantCopy';
import './organisation-connected-apps.scss';

type Feedback = { type: 'success' | 'error'; message: string } | null;

interface OrganisationConnectedAppsProps {
  /** Groups are switched on for this organisation (the backend lists them) */
  groupsAvailable: boolean;
  /**
   * The organisation has a Team Assistant (its tab is shown); the copy names
   * it, otherwise it speaks of the members' own assistants.
   */
  teamAssistantsAvailable: boolean;
  organisationId: string;
  members: OrganisationMember[];
  groups: OrganisationGroup[];
  /** Render the panel (its tab is active). Data loads regardless. */
  visible: boolean;
  /**
   * Whether the organisation can use Connected Apps at all: false while
   * loading and when the backend says the feature is not available (404/403).
   */
  onAvailabilityChange: (available: boolean) => void;
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/** Query parameters the provider callback lands the admin back with. */
const RETURN_PARAMS = ['connected', 'app', 'reason'] as const;

interface ConnectReturn {
  outcome: OrganisationConnectOutcome;
  appKey: string | null;
  reason: string | null;
}

function connectReturn(params: URLSearchParams): ConnectReturn | null {
  const outcome = params.get('connected');
  if (outcome !== 'ok' && outcome !== 'error') return null;
  return { outcome, appKey: params.get('app'), reason: params.get('reason') };
}

const CONNECT_FAILURE_REASONS: Record<string, string> = {
  access_denied: 'access was declined on the provider’s side.',
  state: 'the link had expired or was not for this organisation. Try again.',
  missing_code: 'the provider did not return an authorisation. Try again.',
  connect_not_configured: 'this integration is not configured on Dream yet.',
  admin_only: 'only an active organisation admin can connect apps.',
};

function connectReturnFeedback(
  returned: ConnectReturn,
  apps: OrganisationConnectedApp[],
  groupsAvailable: boolean,
  assistant: AssistantCopy,
): Feedback {
  const label = apps.find((a) => a.key === returned.appKey)?.label ?? 'The app';
  if (returned.outcome === 'ok') {
    return {
      type: 'success',
      message: `${label} connected. ${assistant.Each} can use it${assistant.teamAssistant ? ' for everyone' : ''} at the level below; adjust it per ${groupsAvailable ? 'group or member' : 'member'} if needed.`,
    };
  }
  const why =
    (returned.reason && CONNECT_FAILURE_REASONS[returned.reason]) ??
    `something went wrong${returned.reason ? ` (${returned.reason})` : ''}. Try again.`;
  return {
    type: 'error',
    message: `${label} was not connected: ${why}`,
  };
}

/** "Same as organisation" is the absence of a grant. */
const INHERIT = '';

function levelLabel(
  app: OrganisationConnectedApp,
  level: ConnectedAppAccessLevel,
): string {
  return (
    app.permissionModel.levels.find((l) => l.level === level)?.label ?? level
  );
}

function connectionStatus(connection: OrganisationConnection | null): {
  label: string;
  tone: 'off' | 'on' | 'warn';
} {
  if (!connection || connection.status === 'revoked') {
    return { label: 'Not connected', tone: 'off' };
  }
  if (connection.status === 'needs_reauth') {
    return { label: 'Needs reconnecting', tone: 'warn' };
  }
  return { label: 'Connected', tone: 'on' };
}

function connectionMeta(connection: OrganisationConnection): string {
  const parts: string[] = [];
  if (connection.externalAccountLabel) {
    parts.push(connection.externalAccountLabel);
  }
  if (connection.status === 'revoked') {
    parts.push(`disconnected ${formatDate(connection.updatedAt)}`);
  } else {
    parts.push(
      `connected ${formatDate(connection.createdAt)}${
        connection.authorisedByAdminName
          ? ` by ${connection.authorisedByAdminName}`
          : ''
      }`,
    );
  }
  return parts.join(' · ');
}

interface LevelSelectProps {
  id: string;
  label: string;
  app: OrganisationConnectedApp;
  value: ConnectedAppAccessLevel | typeof INHERIT;
  inheritedLevel: ConnectedAppAccessLevel | null;
  disabled: boolean;
  onChange: (level: ConnectedAppAccessLevel | null) => void;
}

/**
 * One dropdown per member / group: the app's own levels plus "Same as
 * organisation" (no grant). Saves on change.
 */
const LevelSelect: FC<LevelSelectProps> = ({
  id,
  label,
  app,
  value,
  inheritedLevel,
  disabled,
  onChange,
}) => (
  <select
    id={id}
    className='field-select'
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={(e) =>
      onChange(
        e.target.value === INHERIT
          ? null
          : (e.target.value as ConnectedAppAccessLevel),
      )
    }
  >
    {inheritedLevel !== null && (
      <option value={INHERIT}>
        Same as organisation ({levelLabel(app, inheritedLevel).toLowerCase()})
      </option>
    )}
    {app.permissionModel.levels.map((l) => (
      <option key={l.level} value={l.level}>
        {l.label}
      </option>
    ))}
  </select>
);

/**
 * Admin view of the apps Reve can use on the organisation's behalf: one
 * connection per app, one access level for everyone by default, and per-group
 * or per-member levels on top. Levels are the app's own (e.g. accounting apps
 * distinguish "own expenses only" from full access); tools are never shown.
 * A member's own assistant uses the organisation connection at the level set
 * here (No access leaves them on their own connection); the backend decides
 * which apps are listed (the launch set, or every app behind a flag).
 */
const OrganisationConnectedApps: FC<OrganisationConnectedAppsProps> = ({
  groupsAvailable,
  teamAssistantsAvailable,
  organisationId,
  members,
  groups,
  visible,
  onAvailabilityChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const base = `/organisations/${organisationId}/connected-apps`;
  const assistant = assistantCopy(teamAssistantsAvailable);
  // Captured once: the callback's outcome is reported after the list loads,
  // and the parameters are dropped from the URL so a reload does not repeat it.
  const [returned] = useState(() => connectReturn(searchParams));

  const [apps, setApps] = useState<OrganisationConnectedApp[] | null>(null);
  const [grants, setGrants] = useState<OrganisationConnectedAppGrant[]>([]);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!returned) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        RETURN_PARAMS.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
  }, [returned, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<OrganisationConnectedAppsResponse>(
          base,
          await authHeaders(),
        );
        if (cancelled) return;
        setApps(data.apps);
        setGrants(data.grants);
        if (returned) {
          setFeedback(
            connectReturnFeedback(
              returned,
              data.apps,
              groupsAvailable,
              assistant,
            ),
          );
        }
        onAvailabilityChange(true);
      } catch (err) {
        if (cancelled) return;
        const status = httpStatus(err);
        if (status !== 404 && status !== 403) {
          console.error('Error loading connected apps:', err);
        }
        onAvailabilityChange(false);
      }
    })();
    return () => {
      cancelled = true;
      onAvailabilityChange(false);
    };
  }, [base, authHeaders, onAvailabilityChange, returned, groupsAvailable]);

  const replaceConnection = (connection: OrganisationConnection) => {
    setApps((current) =>
      current
        ? current.map((a) =>
            a.key === connection.appKey ? { ...a, connection } : a,
          )
        : current,
    );
  };

  const run = async (key: string, action: () => Promise<string>) => {
    setBusyKey(key);
    setFeedback(null);
    try {
      const message = await action();
      setFeedback({ type: 'success', message });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: apiErrorMessage(err, 'Could not save. Please try again.'),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const changeDefault = (
    app: OrganisationConnectedApp,
    level: ConnectedAppAccessLevel,
  ) =>
    run(`${app.key}:default`, async () => {
      const body: UpdateOrganisationConnectionRequest = {
        defaultLevel: level,
      };
      const { data } = await api.patch<OrganisationConnectionResponse>(
        `${base}/${app.key}`,
        body,
        await authHeaders(),
      );
      replaceConnection(data.connection);
      return `${app.label}: everyone without their own setting now has ${levelLabel(
        app,
        level,
      ).toLowerCase()}.`;
    });

  const changeGrant = (
    app: OrganisationConnectedApp,
    target:
      | { groupId: string; name: string }
      | { membershipId: string; name: string },
    level: ConnectedAppAccessLevel | null,
  ) => {
    const path =
      'groupId' in target
        ? `${base}/${app.key}/groups/${target.groupId}`
        : `${base}/${app.key}/members/${target.membershipId}`;
    return run(path, async () => {
      const body: SetOrganisationConnectedAppGrantRequest = { level };
      const { data } = await api.put<OrganisationConnectedAppGrantResponse>(
        path,
        body,
        await authHeaders(),
      );
      setGrants((current) => {
        const rest = current.filter(
          (g) =>
            g.appKey !== app.key ||
            ('groupId' in target
              ? g.groupId !== target.groupId
              : g.membershipId !== target.membershipId),
        );
        return data.grant ? [...rest, data.grant] : rest;
      });
      return level === null
        ? `${target.name} now follows the organisation setting for ${app.label}.`
        : `${target.name}: ${levelLabel(app, level).toLowerCase()} to ${app.label}.`;
    });
  };

  /**
   * Connect / reconnect: the backend hands back the provider's consent URL and
   * the browser leaves for it; the provider callback returns to this tab.
   */
  const connect = (app: OrganisationConnectedApp) =>
    run(`${app.key}:connect`, async () => {
      const { headers } = await authHeaders();
      const { data } = await api.get<OrganisationConnectStartResponse>(
        `${base}/${app.key}/connect`,
        { headers: { ...headers, Accept: 'application/json' } },
      );
      window.location.assign(data.url);
      return `Taking you to ${app.label} to authorise ${assistant.who}…`;
    });

  const disconnect = (app: OrganisationConnectedApp) =>
    run(`${app.key}:disconnect`, async () => {
      const { data } = await api.delete<OrganisationConnectionResponse>(
        `${base}/${app.key}`,
        await authHeaders(),
      );
      replaceConnection(data.connection);
      setDisconnecting(null);
      return `${app.label} disconnected. ${assistant.Who} can no longer use it${assistant.teamAssistant ? ' for anyone' : ''}; the access settings are kept for when it is connected again.`;
    });

  if (!visible || apps === null) return null;

  const connectable = apps.filter((a) => a.connectable);
  const memberOnly = apps.filter((a) => !a.connectable);
  const activeMembers = members.filter((m) => m.status === 'active');
  const grantFor = (
    appKey: string,
    target: { groupId: string } | { membershipId: string },
  ) =>
    grants.find(
      (g) =>
        g.appKey === appKey &&
        ('groupId' in target
          ? g.groupId === target.groupId
          : g.membershipId === target.membershipId),
    ) ?? null;

  return (
    <div
      className='organisation-card organisation-connected-apps'
      data-testid='organisation-connected-apps'
    >
      <h3 className='organisation-card-title'>Connected apps</h3>
      <p className='organisation-card-text'>
        Connect your business apps once and {assistant.who}{' '}
        {assistant.agree('uses', 'use')} them for the whole team — members never
        need their own logins or API keys. Set who gets which level of access
        per app; the organisation-wide level also applies to anyone who joins
        later. A member’s own secretary uses the organisation’s connection at
        the level you set here; set it to No access to leave them on their own
        connection.
      </p>

      {feedback && (
        <div
          className={`organisation-feedback ${feedback.type}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
        >
          {feedback.message}
        </div>
      )}

      <ul className='organisation-group-list'>
        {connectable.map((app) => {
          const connection = app.connection;
          const live = connection !== null && connection.status !== 'revoked';
          const status = connectionStatus(connection);
          const defaultLevel = connection?.defaultLevel ?? null;
          return (
            <li
              key={app.key}
              className='organisation-group-item'
              data-testid={`connected-app-${app.key}`}
            >
              <div className='organisation-group-row'>
                <div className='organisation-group-info'>
                  <span className='organisation-group-name'>{app.label}</span>
                  <span className='organisation-group-meta'>
                    <span
                      className={`organisation-connected-app-status ${status.tone}`}
                    >
                      {status.label}
                    </span>
                    {connection ? ` · ${connectionMeta(connection)}` : ''}
                    {connection?.status === 'needs_reauth' &&
                    connection.lastError
                      ? ` · ${connection.lastError}`
                      : ''}
                  </span>
                </div>
                {(live || app.connectVia !== null) && (
                  <div className='organisation-group-actions'>
                    {app.connectVia !== null &&
                      (!live || connection?.status === 'needs_reauth') &&
                      disconnecting !== app.key && (
                        <button
                          type='button'
                          className='btn-secondary'
                          disabled={busyKey !== null}
                          onClick={() => connect(app)}
                        >
                          {busyKey === `${app.key}:connect`
                            ? 'Opening…'
                            : live
                              ? 'Reconnect'
                              : 'Connect'}
                        </button>
                      )}
                    {!live ? null : disconnecting === app.key ? (
                      <>
                        <span className='organisation-group-meta'>
                          Disconnect {app.label} for everyone?
                        </span>
                        <button
                          type='button'
                          className='btn-secondary'
                          disabled={busyKey !== null}
                          onClick={() => disconnect(app)}
                        >
                          {busyKey === `${app.key}:disconnect`
                            ? 'Disconnecting…'
                            : 'Yes, disconnect'}
                        </button>
                        <button
                          type='button'
                          className='btn-link'
                          disabled={busyKey !== null}
                          onClick={() => setDisconnecting(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type='button'
                        className='btn-link'
                        disabled={busyKey !== null}
                        onClick={() => setDisconnecting(app.key)}
                      >
                        Disconnect
                      </button>
                    )}
                  </div>
                )}
              </div>

              {live && defaultLevel !== null && (
                <>
                  <label className='organisation-field'>
                    <span className='field-label'>
                      Everyone in the organisation
                    </span>
                    <select
                      className='field-select'
                      aria-label={`${app.label}: access for everyone`}
                      value={defaultLevel}
                      disabled={busyKey !== null}
                      onChange={(e) =>
                        changeDefault(
                          app,
                          e.target.value as ConnectedAppAccessLevel,
                        )
                      }
                    >
                      {app.permissionModel.levels.map((l) => (
                        <option key={l.level} value={l.level}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                    <span className='organisation-group-meta'>
                      {app.permissionModel.levels.find(
                        (l) => l.level === defaultLevel,
                      )?.description ?? ''}{' '}
                      Applies to every member without their own setting below,
                      including people who join later.
                    </span>
                  </label>

                  {groupsAvailable && groups.length > 0 && (
                    <details className='organisation-details'>
                      <summary>Per group ({groups.length})</summary>
                      <ul className='organisation-group-list'>
                        {groups.map((group) => {
                          const grant = grantFor(app.key, {
                            groupId: group.id,
                          });
                          return (
                            <li
                              key={group.id}
                              className='organisation-group-item'
                              data-testid={`connected-app-${app.key}-group-${group.id}`}
                            >
                              <div className='organisation-group-row'>
                                <div className='organisation-group-info'>
                                  <span className='organisation-group-name'>
                                    {group.name}
                                  </span>
                                  <span className='organisation-group-meta'>
                                    Members of this group get this level unless
                                    they have their own setting. A member in
                                    several groups gets the most permissive one.
                                  </span>
                                </div>
                                <LevelSelect
                                  id={`ca-${app.key}-group-${group.id}`}
                                  label={`${app.label}: access for group ${group.name}`}
                                  app={app}
                                  value={grant?.level ?? INHERIT}
                                  inheritedLevel={defaultLevel}
                                  disabled={busyKey !== null}
                                  onChange={(level) =>
                                    changeGrant(
                                      app,
                                      { groupId: group.id, name: group.name },
                                      level,
                                    )
                                  }
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}

                  <details className='organisation-details'>
                    <summary>Per member ({activeMembers.length})</summary>
                    {activeMembers.length === 0 ? (
                      <p className='organisation-empty'>No active members.</p>
                    ) : (
                      <ul className='organisation-group-list'>
                        {activeMembers.map((member) => {
                          const grant = grantFor(app.key, {
                            membershipId: member.id,
                          });
                          return (
                            <li
                              key={member.id}
                              className='organisation-group-item'
                              data-testid={`connected-app-${app.key}-member-${member.id}`}
                            >
                              <div className='organisation-group-row'>
                                <div className='organisation-group-info'>
                                  <span className='organisation-group-name'>
                                    {member.name}
                                  </span>
                                </div>
                                <LevelSelect
                                  id={`ca-${app.key}-member-${member.id}`}
                                  label={`${app.label}: access for ${member.name}`}
                                  app={app}
                                  value={grant?.level ?? INHERIT}
                                  inheritedLevel={defaultLevel}
                                  disabled={busyKey !== null}
                                  onChange={(level) =>
                                    changeGrant(
                                      app,
                                      {
                                        membershipId: member.id,
                                        name: member.name,
                                      },
                                      level,
                                    )
                                  }
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </details>

                  {app.permissionModel.key === 'accounting' && (
                    <OrganisationConnectedAppIdentities
                      organisationId={organisationId}
                      appKey={app.key}
                      appLabel={app.label}
                      teamAssistantsAvailable={teamAssistantsAvailable}
                      disabled={busyKey !== null}
                    />
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {memberOnly.length > 0 && (
        <p className='organisation-group-meta organisation-connected-apps-footnote'>
          {memberOnly.map((a) => a.label).join(', ')}: each member connects
          their own account; there is no organisation-wide connection.
        </p>
      )}
    </div>
  );
};

export default OrganisationConnectedApps;
